/*
 * <product-recommendations-loader>
 * Loads a section's product recommendations. Shopify only knows the recommendations when the section
 * is requested through the recommendations route, so the section first renders this element empty.
 * Once it comes within 400px of the viewport, the element fetches `data-url` (the recommendations
 * route with `product_id`, `section_id`, `limit` and `intent`) and swaps in the markup of the same
 * element from the response. It stays empty when there are no recommendations.
 *
 * Scripts inside the fetched markup don't run, so components used there (e.g. <carousel-slider>)
 * must be loaded by the section, outside this element.
 *
 * Usage:
 *   <product-recommendations-loader data-url="/recommendations/products?product_id=1&section_id=…">
 *   </product-recommendations-loader>
 *
 * Events:
 *   'recommendations:loaded' -> detail: { empty } (fired on the element after each load)
 */
if (!customElements.get("product-recommendations-loader")) {
  class ProductRecommendationsLoader extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;

      this.abortController = new AbortController();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: "0px 0px 400px 0px" },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url, {
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(
          await response.text(),
          "text/html",
        );
        const loaded = html.querySelector(this.localName);
        const content = loaded?.innerHTML.trim() ?? "";

        if (content) this.innerHTML = content;

        this.dispatchEvent(
          new CustomEvent("recommendations:loaded", {
            bubbles: true,
            detail: { empty: !content },
          }),
        );
      } catch (error) {
        if (error.name !== "AbortError") console.error(error);
      }
    }
  }

  customElements.define("product-recommendations-loader", ProductRecommendationsLoader);
}
